import { Head, Link, usePage } from "@inertiajs/react"
import {
  Accessibility,
  Baby,
  Banknote,
  Bath,
  ChevronRight,
  CircleHelp,
  Clock,
  Compass,
  Dog,
  ExternalLink,
  Globe,
  Images,
  MapPin,
  ParkingCircle,
  Phone,
  ShieldCheck,
  ShipWheel,
  SunMedium,
  Ticket,
  Trees,
  Umbrella,
  Utensils,
  Waves,
  type LucideIcon,
} from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { MapCanvas } from "@/components/MapCanvas"
import {
  FerryPortSchedule,
  FerryPortWebcams,
  type FerryPortData,
} from "@/components/FerryPortDetails"
import { captureAnalyticsEvent } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"
import {
  canonicalHref,
  localePathsFrom,
  publicMetaDescription,
  type AlternateLink,
  type PublicLocale,
} from "@/lib/public-links"

type Place = {
  id: number
  name: string
  alternate_name: string | null
  slug: string
  summary: string
  body: string | null
  meta_title: string | null
  meta_description: string | null
  category: string
  category_key: string
  category_icon: string | null
  category_slug: string
  destination: string
  destination_slug: string
  destination_path: string
  address: string | null
  phone: string | null
  latitude: number | null
  longitude: number | null
  path: string
}

type Photo = {
  id: number
  alt_text: string
  credit: string | null
  primary: boolean
  card_url: string
  hero_url: string
  gallery_url: string
}

type Fact = {
  key?: string
  label: string
  value: string
  verification:
    | { status: "unverified"; aspect: string; visitor_critical: boolean }
    | {
        status: "verified"
        aspect: string
        source_name: string
        source_url: string | null
        confidence: "high" | "medium" | "low"
        verified_at: string
      }
}

type NearbyPlace = Place & {
  path: string
  photo?: Photo | null
  straight_line_distance_meters: number
}

type OutboundLink = {
  label: string
  href: string
  host?: string | null
  tracking_path: string
}

const COPY = {
  en: {
    home: "Home",
    facts: "The facts",
    verificationPending: "Not yet verified",
    verificationExplanation: "We have not checked this ourselves yet.",
    verified: "Verified",
    verificationRecord: "Verification record",
    source: "Source",
    checked: "Checked",
    confidence: "Confidence",
    confidenceHigh: "High",
    confidenceMedium: "Medium",
    confidenceLow: "Low",
    reportPending: "Spot something wrong or outdated? Use Report a problem to tell the editors.",
    mediaPending: "Editorial photos are being prepared",
    mediaPendingText: "We will show place photography only after it has been uploaded, credited, and reviewed.",
    photos: "Photos",
    staticPreview: "Static preview",
    openMap: "Open map",
    nearby: "Nearby",
    nearbyHint: "Distances are straight-line distance, not walking time.",
    officialLinks: "Official links",
    externalSite: "external site",
    contact: "Contact",
    address: "Address",
    phone: "Phone",
    distanceLabel: "straight line",
  },
  de: {
    home: "Startseite",
    facts: "Die Fakten",
    verificationPending: "Noch nicht überprüft",
    verificationExplanation: "Wir haben diese Angabe noch nicht selbst überprüft.",
    verified: "Überprüft",
    verificationRecord: "Prüfnachweis",
    source: "Quelle",
    checked: "Geprüft",
    confidence: "Vertrauen",
    confidenceHigh: "Hoch",
    confidenceMedium: "Mittel",
    confidenceLow: "Niedrig",
    reportPending: "Ist etwas falsch oder veraltet? Sende der Redaktion einen Hinweis über Problem melden.",
    mediaPending: "Redaktionelle Fotos werden vorbereitet",
    mediaPendingText: "Fotos erscheinen erst, nachdem sie hochgeladen, mit einer Quellenangabe versehen und geprüft wurden.",
    photos: "Fotos",
    staticPreview: "Statische Vorschau",
    openMap: "Karte öffnen",
    nearby: "In der Nähe",
    nearbyHint: "Entfernungen sind Luftlinie, keine Gehzeit.",
    officialLinks: "Offizielle Links",
    externalSite: "externe Seite",
    contact: "Kontakt",
    address: "Adresse",
    phone: "Telefon",
    distanceLabel: "Luftlinie",
  },
} satisfies Record<PublicLocale, Record<string, string>>

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  beach: Waves,
  waves: Waves,
  restaurant: Utensils,
  "cafe-bar": Utensils,
  pharmacy: ShieldCheck,
  doctor: ShieldCheck,
  supermarket: Utensils,
  parking: ParkingCircle,
  marina: ShipWheel,
  "ferry-port": ShipWheel,
  viewpoint: MapPin,
  rental: Compass,
  "tours-rentals": Compass,
  campsite: Trees,
  bakery: Utensils,
  "atm-exchange": Banknote,
}

const FACT_ICONS: Record<string, LucideIcon> = {
  beach_material: Waves,
  sea_floor_material: Waves,
  shade_level: Umbrella,
  dogs_allowed: Dog,
  fkk_allowed: SunMedium,
  kid_friendly: Baby,
  wheelchair_accessible: Accessibility,
  toilets: Bath,
  showers: Bath,
  food_nearby: Utensils,
  parking_nearby: ParkingCircle,
  entry_fee_note: Ticket,
  opening_hours: Clock,
  seasonal_note: SunMedium,
}

export default function PlaceShow({
  locale,
  place,
  facts,
  photos,
  nearby_places,
  alternate_links,
  outbound_links,
  ferry_port,
}: {
  locale: PublicLocale
  place: Place
  facts: Fact[]
  photos: Photo[]
  nearby_places: NearbyPlace[]
  alternate_links: AlternateLink[]
  outbound_links: Record<string, OutboundLink>
  ferry_port: FerryPortData | null
}) {
  const copy = COPY[locale]
  const canonical = canonicalHref(alternate_links, locale)
  const outboundEntries = Object.entries(outbound_links)
  const CategoryIcon = iconForPlace(place)
  const heroPhoto = photos.find((photo) => photo.primary) ?? photos[0]
  const galleryPhotos = photos.filter((photo) => photo.id !== heroPhoto?.id)
  const title = place.meta_title || place.name
  const description =
    place.meta_description ||
    publicMetaDescription(
      place.summary,
      locale === "de"
        ? `Praktische Angaben, Lage, Luftlinien zu nahen Orten und klar gekennzeichneter Prüfstatus für ${place.name} auf ${place.destination}.`
        : `Practical details, location, straight-line distances to nearby places, and clearly labelled verification status for ${place.name} on ${place.destination}.`,
    )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {canonical && <link rel="canonical" href={canonical} />}
        {alternate_links.map((link) => (
          <link key={link.href_lang} rel="alternate" hrefLang={link.href_lang} href={link.href} />
        ))}
      </Head>

      <PublicShell locale={locale} localePaths={localePathsFrom(alternate_links)}>
        <main>
          <section className="public-container pt-4 md:pt-6">
            <Breadcrumbs locale={locale} place={place} homeLabel={copy.home} />
            {ferry_port
              ? <FerryPortWebcams locale={locale} ferryPort={ferry_port} />
              : heroPhoto
                ? <PhotoHero photo={heroPhoto} />
                : <MediaPending copy={copy} />}
          </section>

          <section className="public-container grid gap-6 py-5 md:grid-cols-[1fr_23.75rem] md:gap-8 md:py-6">
            <div className="min-w-0">
              <h1 className="public-page-title">
                {place.name}
                {place.alternate_name && (
                  <span className="ml-1 text-[1.12rem] font-medium text-ink-soft md:text-[1.45rem]">· {place.alternate_name}</span>
                )}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-muted">
                <CategoryIcon className="h-4 w-4 text-accent" aria-hidden />
                <span>{place.category}</span>
                <span aria-hidden>·</span>
                <Link href={place.destination_path} className="text-ink-muted no-underline hover:text-ink-display">{place.destination}</Link>
              </div>
              <p className="public-lede mt-3">{place.summary}</p>
              {place.body && <p className="public-lede mt-5">{place.body}</p>}

              {ferry_port && <FerryPortSchedule locale={locale} ferryPort={ferry_port} />}
              {facts.length > 0 && <FactsPanel facts={facts} copy={copy} />}
              {galleryPhotos.length > 0 && <PhotoGallery photos={galleryPhotos} title={copy.photos} />}
            </div>

            <aside className="space-y-5">
              <StaticMapPreview place={place} copy={copy} />
              {(place.address || place.phone) && <ContactPanel place={place} copy={copy} />}
              {nearby_places.length > 0 && <NearbyList nearbyPlaces={nearby_places} copy={copy} locale={locale} />}
              {outboundEntries.length > 0 && <OfficialLinks links={outboundEntries} copy={copy} placeId={place.id} />}
            </aside>
          </section>
        </main>
      </PublicShell>
    </>
  )
}

function PhotoHero({ photo }: { photo: Photo }) {
  return (
    <figure className="relative mt-3 overflow-hidden rounded-2xl border border-hairline-strong bg-surface-subtle">
      <img src={photo.hero_url} alt={photo.alt_text} loading="lazy" className="h-[14.375rem] w-full object-cover md:h-[31rem]" />
      {photo.credit && <figcaption className="absolute right-3 bottom-3 rounded-full bg-ink-display/75 px-3 py-1 text-xs text-surface">{photo.credit}</figcaption>}
    </figure>
  )
}

function PhotoGallery({ photos, title }: { photos: Photo[]; title: string }) {
  return (
    <section className="mt-6">
      <h2 className="public-section-title">{title}</h2>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
        {photos.map((photo) => (
          <figure key={photo.id} className="relative overflow-hidden rounded-xl border border-hairline-strong bg-surface">
            <img src={photo.gallery_url} alt={photo.alt_text} loading="lazy" className="aspect-[4/3] w-full object-cover" />
            {photo.credit && <figcaption className="absolute inset-x-0 bottom-0 bg-ink-display/75 px-3 py-2 text-xs text-surface">{photo.credit}</figcaption>}
          </figure>
        ))}
      </div>
    </section>
  )
}

function MediaPending({ copy }: { copy: (typeof COPY)[PublicLocale] }) {
  return (
    <section className="mt-3 flex min-h-[14.375rem] items-center justify-center rounded-2xl border border-dashed border-hairline bg-surface-subtle px-6 text-center md:min-h-[25rem]">
      <div className="max-w-md">
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-surface text-accent">
          <Images className="h-5 w-5" aria-hidden />
        </span>
        <h2 className="public-card-title mt-4">{copy.mediaPending}</h2>
        <p className="public-card-description mt-2">{copy.mediaPendingText}</p>
      </div>
    </section>
  )
}

function Breadcrumbs({ locale, place, homeLabel }: { locale: PublicLocale; place: Place; homeLabel: string }) {
  return (
    <nav className="flex flex-wrap items-center gap-1.5 text-[0.78rem] text-ink-muted" aria-label="Breadcrumb">
      <Link href={`/${locale}`} className="text-ink-muted no-underline hover:text-ink-display">{homeLabel}</Link>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <Link href={place.destination_path} className="text-ink-muted no-underline hover:text-ink-display">{place.destination}</Link>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <span>{place.category}</span>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <span className="font-semibold text-ink-secondary">{place.name}</span>
    </nav>
  )
}

function FactsPanel({ facts, copy }: { facts: Fact[]; copy: (typeof COPY)[PublicLocale] }) {
  return (
    <section className="public-panel mt-5 overflow-hidden">
      <div className="flex items-baseline justify-between gap-3 px-4 pt-4 pb-2 md:px-5">
        <h2 className="public-section-title">{copy.facts}</h2>
      </div>
      <dl>
        {facts.map((fact) => {
          const Icon = FACT_ICONS[fact.key ?? ""] ?? CircleHelp
          return (
            <div key={`${fact.key ?? fact.label}-${fact.value}`} className="relative grid grid-cols-[auto_minmax(0,1fr)] gap-3 border-t border-surface-subtle px-4 py-3.5 md:grid-cols-[auto_minmax(0,1fr)_auto] md:px-5">
              <Icon className="mt-1 h-[1.1rem] w-[1.1rem] shrink-0 text-ink-soft" aria-hidden />
              <div className="min-w-0 flex-1 md:grid md:grid-cols-[10.5rem_1fr] md:gap-3">
                <dt className="text-[0.69rem] font-semibold uppercase tracking-[0.07em] text-ink-soft">{fact.label}</dt>
                <dd className="mt-1 text-[0.94rem] leading-snug text-ink-body md:mt-0">{fact.value}</dd>
              </div>
              <VerificationBadge verification={fact.verification} copy={copy} />
            </div>
          )
        })}
      </dl>
      <p className="public-compact-note border-t border-surface-subtle px-4 py-3 md:px-5">{copy.reportPending}</p>
    </section>
  )
}

function VerificationBadge({
  verification,
  copy,
}: {
  verification: Fact["verification"]
  copy: (typeof COPY)[PublicLocale]
}) {
  if (verification.status === "unverified") {
    return (
      <span
        className="col-start-2 mt-0.5 inline-flex w-fit shrink-0 items-center gap-1 rounded-full border border-dashed border-signal/45 bg-signal-faded px-2.5 py-1 text-[0.68rem] font-semibold text-signal md:col-start-3 md:row-start-1"
        title={copy.verificationExplanation}
      >
        <CircleHelp className="h-3 w-3" aria-hidden />
        {copy.verificationPending}
      </span>
    )
  }

  const confidenceLabel = {
    high: copy.confidenceHigh,
    medium: copy.confidenceMedium,
    low: copy.confidenceLow,
  }[verification.confidence]
  const checkedDate = formatVerificationDate(verification.verified_at, copy === COPY.de ? "de-DE" : "en-GB")

  return (
    <details className="group col-start-2 mt-0.5 w-fit max-w-full md:col-start-3 md:row-start-1">
      <summary className={`inline-flex cursor-pointer list-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-[0.68rem] font-semibold [&::-webkit-details-marker]:hidden ${verificationTone(verification.confidence)}`}>
        <ShieldCheck className="h-3 w-3 shrink-0" aria-hidden />
        <span>{copy.verified} · {verification.source_name} · {checkedDate} · {confidenceLabel}</span>
      </summary>
      <div className="absolute right-4 z-20 mt-2 w-[min(17rem,calc(100%-2rem))] rounded-xl border border-hairline bg-surface p-4 text-left shadow-lg md:right-5">
        <p className="text-[0.63rem] font-semibold uppercase tracking-[0.09em] text-ink-faint">{copy.verificationRecord}</p>
        <dl className="mt-3 grid grid-cols-[5rem_1fr] gap-x-3 gap-y-2 text-xs">
          <dt className="text-ink-muted">{copy.source}</dt>
          <dd className="text-ink-body">
            {verification.source_url ? (
              <a href={verification.source_url} target="_blank" rel="noreferrer">{verification.source_name}</a>
            ) : verification.source_name}
          </dd>
          <dt className="text-ink-muted">{copy.checked}</dt>
          <dd className="text-ink-body">{checkedDate}</dd>
          <dt className="text-ink-muted">{copy.confidence}</dt>
          <dd className="text-ink-body">{confidenceLabel}</dd>
        </dl>
      </div>
    </details>
  )
}

function verificationTone(confidence: "high" | "medium" | "low") {
  if (confidence === "high") return "border-verified bg-verified text-surface"
  if (confidence === "medium") return "border-verified bg-surface text-verified"
  return "border-hairline bg-surface-subtle text-ink-secondary"
}

function formatVerificationDate(isoDate: string, locale: "en-GB" | "de-DE") {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(
    new Date(`${isoDate}T00:00:00Z`),
  )
}


function StaticMapPreview({ place, copy }: { place: Place; copy: (typeof COPY)[PublicLocale] }) {
  const { props } = usePage<PageProps>()
  const hasCoordinates = place.latitude !== null && place.longitude !== null

  return (
    <section className="public-panel overflow-hidden" aria-label={copy.openMap}>
      {hasCoordinates ? (
        <MapCanvas maptilerKey={props.maptiler_key} center={[ place.longitude!, place.latitude! ]} zoom={14} marker className="h-[14.375rem]" />
      ) : (
        <div className="flex h-[14.375rem] items-center justify-center bg-surface-subtle text-sm text-ink-muted">{copy.staticPreview}</div>
      )}
    </section>
  )
}

function ContactPanel({ place, copy }: { place: Place; copy: (typeof COPY)[PublicLocale] }) {
  return (
    <section className="public-panel p-4">
      <h2 className="public-card-title">{copy.contact}</h2>
      <div className="mt-3 space-y-3">
        {place.address && <ContactRow icon={MapPin} label={copy.address} value={place.address} />}
        {place.phone && <ContactRow icon={Phone} label={copy.phone} value={place.phone} href={`tel:${place.phone}`} />}
      </div>
    </section>
  )
}

function ContactRow({ icon: Icon, label, value, href }: { icon: LucideIcon; label: string; value: string; href?: string }) {
  return (
    <div className="flex gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
      <span>
        <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.07em] text-ink-soft">{label}</span>
        {href ? <a href={href} className="public-contact-link">{value}</a> : <span className="block text-sm text-ink-display">{value}</span>}
      </span>
    </div>
  )
}

function NearbyList({ nearbyPlaces, copy, locale }: { nearbyPlaces: NearbyPlace[]; copy: (typeof COPY)[PublicLocale]; locale: PublicLocale }) {
  return (
    <section>
      <h2 className="public-card-title">{copy.nearby}</h2>
      <p className="public-compact-note mt-1">{copy.nearbyHint}</p>
      <div className="mt-3 space-y-2.5">
        {nearbyPlaces.map((nearby) => (
          <Link key={nearby.id} href={nearby.path} className="public-card group block overflow-hidden no-underline">
            {nearby.photo && <img src={nearby.photo.card_url} alt={nearby.photo.alt_text} loading="lazy" className="aspect-[3/2] w-full object-cover" />}
            <div className="p-3">
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-ink-display">{nearby.name}</span>
                <span className="mt-0.5 block truncate text-xs text-ink-muted">{nearby.category}</span>
                <span className="mt-1 block text-xs text-ink-muted">{formatDistance(nearby.straight_line_distance_meters, locale)} · {copy.distanceLabel}</span>
              </span>
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5" aria-hidden />
            </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  )
}

function OfficialLinks({ links, copy, placeId }: { links: Array<[string, OutboundLink]>; copy: (typeof COPY)[PublicLocale]; placeId: number }) {
  return (
    <section>
      <h2 className="public-card-title">{copy.officialLinks}</h2>
      <div className="mt-3 space-y-2.5">
        {links.map(([key, link]) => (
          <a
            key={key}
            href={link.href}
            className="public-card flex items-center gap-3 p-3.5 no-underline hover:border-accent/40"
            onClick={() => {
              captureAnalyticsEvent("outbound_link_clicked", { place_id: placeId, link_kind: key, link_host: link.host })
              navigator.sendBeacon?.(link.tracking_path)
            }}
          >
            <Globe className="h-[1.125rem] w-[1.125rem] shrink-0 text-accent" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-accent">{link.label}</span>
              <span className="block truncate text-xs text-ink-muted">{link.host ?? link.href} · {copy.externalSite}</span>
            </span>
            <ExternalLink className="h-3.5 w-3.5 shrink-0 text-ink-muted" aria-hidden />
          </a>
        ))}
      </div>
    </section>
  )
}

function iconForPlace(place: Place) {
  return CATEGORY_ICONS[place.category_key] ?? CATEGORY_ICONS[place.category_icon ?? ""] ?? MapPin
}

function formatDistance(meters: number, locale: PublicLocale) {
  if (meters < 1000) return `${Math.round(meters)} m`
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(meters / 1000)} km`
}
